import React,{useState,useMemo,useCallback,useEffect} from 'react';
import {PageHeader,FilterBar,DataTable,Modal,Badge,EmptyState,FormSection} from '../components/ui/SharedUI';
import {demoRepository} from '../repository/demoRepository';
import {calculations} from '../utils/calculations';
import {useFeedback} from '../components/ui/FeedbackUI';
import SmartDateInput from '../components/ui/SmartDateInput';

const inspectionTypes=[
{value: 'routine',label: 'Routine'},
{value: 'entry',label: 'Entry'},
{value: 'quarterly',label: 'Quarterly'},
{value: 'exit',label: 'Exit'},
{value: 'condition',label: 'Condition'},
{value: 'maintenance',label: 'Maintenance'},
{value: 'insurance',label: 'Insurance'},
{value: 'other',label: 'Other'}
];

const outcomeOptions=[
{value: 'no_issues',label: 'No Issues'},
{value: 'minor_issues',label: 'Minor Issues'},
{value: 'follow_up_required',label: 'Follow-up Required'},
{value: 'urgent_repair',label: 'Urgent Repair'},
{value: 'tenant_issue',label: 'Tenant Issue'},
{value: 'not_completed',label: 'Not Completed'}
];

const statusOptions=[
{value: 'scheduled',label: 'Scheduled'},
{value: 'completed',label: 'Completed'},
{value: 'cancelled',label: 'Cancelled'},
{value: 'follow_up_required',label: 'Follow-up Required'},
{value: 'archived',label: 'Archived'}
];

const getInspectionTypeLabel=(value)=> inspectionTypes.find(t=> t.value===value)?.label || value;
const getOutcomeLabel=(value)=> outcomeOptions.find(o=> o.value===value)?.label || value;
const getStatusLabel=(value)=> statusOptions.find(s=> s.value===value)?.label || value;

const toDateInput=(value)=> {
if (!value) return new Date().toISOString().split('T')[0];
const d=new Date(value);
return isNaN(d.getTime()) ? new Date().toISOString().split('T')[0] : d.toISOString().split('T')[0];
};

const InspectionsPage=()=> {
const {showToast,showConfirm}=useFeedback();
const [isAddModalOpen,setIsAddModalOpen]=useState(false);
const [editingInspection,setEditingInspection]=useState(null);
const [searchTerm,setSearchTerm]=useState('');
const [typeFilter,setTypeFilter]=useState('All');
const [statusFilter,setStatusFilter]=useState('All');
const [propertyFilter,setPropertyFilter]=useState('All');
const [refreshTrigger,setRefreshTrigger]=useState(0);

useEffect(()=> {
const handleDataRefreshed=()=> {
setRefreshTrigger(prev=> prev + 1);
};
window.addEventListener('mpp:data-refreshed',handleDataRefreshed);
return ()=> window.removeEventListener('mpp:data-refreshed',handleDataRefreshed);
},[]);

const allInspections=useMemo(()=> {
return demoRepository.getInspections();
},[refreshTrigger]);

const properties=demoRepository.getAllProperties().filter(p=> p.status==='Active');
const spaces=properties.flatMap(p=> demoRepository.getSpacesByProperty(p.id));
const leases=demoRepository.getAllProperties().flatMap(property=> demoRepository.getCurrentLeasesByProperty(property.id));

const filteredInspections=useMemo(()=> {
return allInspections.filter(ins=> {
if (typeFilter !=='All' && ins.type !==typeFilter) return false;
if (statusFilter !=='All' && ins.status !==statusFilter) return false;
if (propertyFilter !=='All' && ins.propertyId !==propertyFilter) return false;
if (searchTerm) {
const lower=searchTerm.toLowerCase();
const property=ins.propertyId ? demoRepository.getPropertyById(ins.propertyId) : null;
return (
ins.type?.toLowerCase().includes(lower) ||
property?.name?.toLowerCase().includes(lower) ||
ins.notes?.toLowerCase().includes(lower)
);
}
return true;
});
},[allInspections,searchTerm,typeFilter,statusFilter,propertyFilter]);

const handleAddInspection=useCallback(async (data)=> {
try {
const result=editingInspection
? await demoRepository.updateInspection(editingInspection.id,data)
: await demoRepository.addInspection(data);

if (!result?.success) {
showToast(result?.error || 'Inspection save failed.','error');
return;
}

showToast(
result.mode==='pocketbase'
? 'Inspection saved to PocketBase.'
: 'Inspection saved to demo data.',
'success'
);

setIsAddModalOpen(false);
setEditingInspection(null);
setRefreshTrigger(prev=> prev + 1);
} catch (error) {
showToast(error?.message || 'Inspection save failed.','error');
}
},[editingInspection,showToast]);

const handleEditInspection=useCallback((inspection)=> {
setEditingInspection(inspection);
setIsAddModalOpen(true);
},[]);

const handleArchiveInspection=useCallback((id)=> {
showConfirm({
title: 'Archive Inspection?',
message: 'Are you sure you want to archive this inspection? This will not delete historical data.',
confirmText: 'Archive',
isDestructive: true,
onConfirm: async ()=> {
try {
const result=await demoRepository.updateInspection(id,{status: 'archived'});
if (result?.success) {
showToast('Inspection archived','info');
setRefreshTrigger(prev=> prev + 1);
} else {
showToast(result?.error || 'Archive failed.','error');
}
} catch (error) {
showToast(error?.message || 'Archive failed.','error');
}
}
});
},[showConfirm,showToast]);

const statusColors={
scheduled: 'pending',
completed: 'active',
cancelled: 'default',
follow_up_required: 'pending',
archived: 'default'
};

const columns=['Date','Type','Property','Space','Inspector','Outcome','Issues','Status','Actions'];

const renderRow=(ins)=> {
const property=ins.propertyId ? demoRepository.getPropertyById(ins.propertyId) : null;
const space=ins.spaceId ? demoRepository.getSpaceById(ins.spaceId) : null;
const issuesCount=ins.issues?.length || 0;
return [
<div>
<div className="font-medium text-slate-900">{calculations.formatDateAU(ins.inspectionDate)}</div>
<div className="text-xs text-slate-500">{ins.followupRequired ? 'Follow-up required' : 'No follow-up'}</div>
</div>,
<Badge label={getInspectionTypeLabel(ins.type)} status="default" />,
property?.name || '-',
space?.name || '-',
ins.inspector || '-',
<Badge label={getOutcomeLabel(ins.outcome)} status={ins.outcome==='no_issues' ? 'active' : ins.outcome==='urgent_repair' ? 'overdue' : 'pending'} />,
issuesCount > 0 ? (<Badge label={`${issuesCount} issue${issuesCount !==1 ? 's' : ''}`} status="overdue" />) : (<span className="text-slate-400">None</span>),
<Badge label={getStatusLabel(ins.status)} status={statusColors[ins.status] || 'default'} />,
<div className="flex gap-2">
<button onClick={(e)=> {e.stopPropagation();handleEditInspection(ins);}} className="text-amber-600 hover:underline text-xs font-medium">Edit</button>
<button onClick={(e)=> {e.stopPropagation();handleArchiveInspection(ins.id);}} className="text-red-600 hover:underline text-xs font-medium">Archive</button>
</div>
];
};

return (
<div className="animate-fade-in">
<PageHeader title="Property Inspections" subtitle="Track routine,entry,exit,and maintenance inspections." actionLabel="Add Inspection" onAction={()=> {setEditingInspection(null);setIsAddModalOpen(true);}} />

<FilterBar onSearch={setSearchTerm} onClear={()=> {setSearchTerm('');setTypeFilter('All');setStatusFilter('All');setPropertyFilter('All');}}>
<select value={typeFilter} onChange={(e)=> setTypeFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500">
<option value="All">All Types</option>
{inspectionTypes.map(type=> (<option key={type.value} value={type.value}>{type.label}</option>))}
</select>
<select value={statusFilter} onChange={(e)=> setStatusFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500">
<option value="All">All Statuses</option>
{statusOptions.map(s=> (<option key={s.value} value={s.value}>{s.label}</option>))}
</select>
<select value={propertyFilter} onChange={(e)=> setPropertyFilter(e.target.value)} className="px-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500">
<option value="All">All Properties</option>
{properties.map(p=> (<option key={p.id} value={p.id}>{p.name}</option>))}
</select>
</FilterBar>

{filteredInspections.length > 0 ? (
<DataTable columns={columns} data={filteredInspections} renderRow={renderRow} emptyMessage="No inspections found matching your criteria." />
) : (
<EmptyState title="No Inspections Recorded" description="Add property inspections to track conditions,compliance,and maintenance requirements." actionLabel="Add Inspection" onAction={()=> setIsAddModalOpen(true)} icon="CheckSquare" />
)}

<Modal isOpen={isAddModalOpen} onClose={()=> {setIsAddModalOpen(false);setEditingInspection(null);}} title={editingInspection ? 'Edit Inspection' : 'Add Inspection'} maxWidth="max-w-4xl">
<InspectionForm initialData={editingInspection} properties={properties} spaces={spaces} leases={leases} onSubmit={handleAddInspection} onCancel={()=> {setIsAddModalOpen(false);setEditingInspection(null);}} />
</Modal>
</div>
);
};

const InspectionForm=({initialData,properties,spaces,leases,onSubmit,onCancel})=> {
const [formData,setFormData]=useState({
propertyId: initialData?.propertyId || '',
spaceId: initialData?.spaceId || '',
leaseId: initialData?.leaseId || '',
inspectionDate: toDateInput(initialData?.inspectionDate),
type: initialData?.type || 'routine',
inspector: initialData?.inspector || '',
outcome: initialData?.outcome || 'no_issues',
status: initialData?.status || 'completed',
notes: initialData?.notes || '',
issues: initialData?.issues || [],
followupRequired: initialData?.followupRequired || false,
followupByDate: initialData?.followupByDate ? toDateInput(initialData.followupByDate) : '',
documentIds: initialData?.documentIds || []
});

const [newIssue,setNewIssue]=useState('');

useEffect(()=> {
setFormData({
propertyId: initialData?.propertyId || '',
spaceId: initialData?.spaceId || '',
leaseId: initialData?.leaseId || '',
inspectionDate: toDateInput(initialData?.inspectionDate),
type: initialData?.type || 'routine',
inspector: initialData?.inspector || '',
outcome: initialData?.outcome || 'no_issues',
status: initialData?.status || 'completed',
notes: initialData?.notes || '',
issues: initialData?.issues || [],
followupRequired: initialData?.followupRequired || false,
followupByDate: initialData?.followupByDate ? toDateInput(initialData.followupByDate) : '',
documentIds: initialData?.documentIds || []
});
},[
initialData?.id,
initialData?.updated,
initialData?.inspectionDate,
initialData?.type,
initialData?.outcome,
initialData?.status
]);

const handleChange=(e)=> {
const {name,value,type,checked}=e.target;
setFormData(prev=> ({...prev,[name]: type==='checkbox' ? checked : value}));
};

const handleAddIssue=()=> {
if (newIssue.trim()) {
setFormData(prev=> ({...prev,issues: [...prev.issues,newIssue.trim()]}));
setNewIssue('');
}
};

const handleRemoveIssue=(index)=> {
setFormData(prev=> ({...prev,issues: prev.issues.filter((_,i)=> i !==index)}));
};

const handleSubmit=(e)=> {
e.preventDefault();
onSubmit({
...formData,
inspectionDate: new Date(formData.inspectionDate).getTime(),
followupByDate: formData.followupByDate ? new Date(formData.followupByDate).getTime() : null
});
};

const inputClass="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white";
const labelClass="block text-xs font-medium text-slate-600 mb-1";

return (
<form onSubmit={handleSubmit} className="space-y-6">
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div>
<label className={labelClass}>Property *</label>
<select name="propertyId" value={formData.propertyId} onChange={handleChange} className={inputClass} required>
<option value="">Select property...</option>
{properties.map(p=> (<option key={p.id} value={p.id}>{p.name}</option>))}
</select>
</div>
<div>
<label className={labelClass}>Inspection Date *</label>
<SmartDateInput name="inspectionDate" value={formData.inspectionDate} onChange={handleChange} className={inputClass} required />
</div>
<div>
<label className={labelClass}>Inspection Type *</label>
<select name="type" value={formData.type} onChange={handleChange} className={inputClass} required>
{inspectionTypes.map(type=> (<option key={type.value} value={type.value}>{type.label}</option>))}
</select>
</div>
<div>
<label className={labelClass}>Inspector/Agent</label>
<input type="text" name="inspector" value={formData.inspector} onChange={handleChange} className={inputClass} placeholder="Inspector name" />
</div>
{formData.propertyId && (
<div>
<label className={labelClass}>Room/Space (Optional)</label>
<select name="spaceId" value={formData.spaceId} onChange={handleChange} className={inputClass}>
<option value="">Select space...</option>
{spaces.filter(s=> s.propertyId===formData.propertyId).map(s=> (<option key={s.id} value={s.id}>{s.name}</option>))}
</select>
</div>
)}
<div>
<label className={labelClass}>Related Lease (Optional)</label>
<select name="leaseId" value={formData.leaseId} onChange={handleChange} className={inputClass}>
<option value="">Select lease...</option>
{leases.filter(l=> l.propertyId===formData.propertyId).map(l=> {const tenant=demoRepository.getTenantById(l.tenantId);return (<option key={l.id} value={l.id}>{tenant?.firstName} {tenant?.lastName}</option>);})}
</select>
</div>
<div>
<label className={labelClass}>Outcome</label>
<select name="outcome" value={formData.outcome} onChange={handleChange} className={inputClass}>
{outcomeOptions.map(o=> (<option key={o.value} value={o.value}>{o.label}</option>))}
</select>
</div>
<div>
<label className={labelClass}>Status</label>
<select name="status" value={formData.status} onChange={handleChange} className={inputClass}>
{statusOptions.map(s=> (<option key={s.value} value={s.value}>{s.label}</option>))}
</select>
</div>
<div className="md:col-span-2">
<label className={labelClass}>Condition Notes</label>
<textarea name="notes" value={formData.notes} onChange={handleChange} className={inputClass} rows="3" placeholder="General condition notes..." />
</div>
<div className="md:col-span-2">
<label className={labelClass}>Issues Found</label>
<div className="flex gap-2 mb-2">
<input type="text" value={newIssue} onChange={(e)=> setNewIssue(e.target.value)} className={`${inputClass} flex-1`} placeholder="Add an issue..." onKeyPress={(e)=> e.key==='Enter' && (e.preventDefault(),handleAddIssue())} />
<button type="button" onClick={handleAddIssue} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg">Add</button>
</div>
{formData.issues.length > 0 && (
<div className="space-y-1 max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2">
{formData.issues.map((issue,index)=> (
<div key={index} className="flex justify-between items-center text-sm p-1 bg-slate-50 rounded">
<span>{issue}</span>
<button type="button" onClick={()=> handleRemoveIssue(index)} className="text-red-600 hover:underline text-xs">Remove</button>
</div>
))}
</div>
)}
</div>
<div className="md:col-span-2">
<label className={labelClass}>
<input type="checkbox" name="followupRequired" checked={formData.followupRequired} onChange={handleChange} className="mr-2" />
Follow-up Required
</label>
</div>
{formData.followupRequired && (
<div>
<label className={labelClass}>Follow-up By Date</label>
<SmartDateInput name="followupByDate" value={formData.followupByDate} onChange={handleChange} className={inputClass} baseDate={formData.inspectionDate} baseLabel="the inspection date" />
</div>
)}
</div>
<div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
<button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
<button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg">Save Inspection</button>
</div>
</form>
);
};

export default InspectionsPage;